import styled from "styled-components";
import { theme } from "styles/theme";

export const AlarmsContainer = styled.div`
  position: relative;
`;

export const AlarmTypeChip = styled.span<{ type: string }>`
  padding: 0.4rem 0.75rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 2px solid;
  background-color: ${(props) => {
    if (props.type.includes("ERROR")) {
      return "#ffe6e9"; // very light red for errors
    } else if (props.type.includes("WARN")) {
      return "#fff8e1"; // very light yellow for warnings
    } else if (props.type.includes("HIGH")) {
      return "#ffebee"; // light red for high values
    } else if (props.type.includes("LOW")) {
      return "#e3f2fd"; // light blue for low values
    } else {
      return "#f5f5f5"; // light gray for others
    }
  }};
  color: ${(props) => {
    if (props.type.includes("ERROR")) {
      return "#c62828"; // dark red for errors
    } else if (props.type.includes("WARN")) {
      return "#f57c00"; // dark orange for warnings
    } else if (props.type.includes("HIGH")) {
      return "#d32f2f"; // red for high values
    } else if (props.type.includes("LOW")) {
      return "#1976d2"; // blue for low values
    } else {
      return "#424242"; // dark gray for others
    }
  }};
  border-color: ${(props) => {
    if (props.type.includes("ERROR")) {
      return "#ef5350";
    } else if (props.type.includes("WARN")) {
      return "#ff9800";
    } else if (props.type.includes("HIGH")) {
      return "#f44336";
    } else if (props.type.includes("LOW")) {
      return "#2196f3";
    } else {
      return "#9e9e9e";
    }
  }};
`;

export const ReadStatusChip = styled.div<{ isRead: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${(props) => (props.isRead ? "#28a745" : "#dc3545")};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${(props) => (props.isRead ? "#28a745" : "#dc3545")};
    box-shadow: ${(props) =>
      props.isRead
        ? "0 0 0 2px rgba(40, 167, 69, 0.2)"
        : "0 0 0 2px rgba(220, 53, 69, 0.2)"};
    animation: ${(props) => (props.isRead ? "none" : "pulse-dot 2s infinite")};
  }

  @keyframes pulse-dot {
    0% {
      box-shadow: ${(props) =>
        props.isRead
          ? "0 0 0 2px rgba(40, 167, 69, 0.2)"
          : "0 0 0 2px rgba(220, 53, 69, 0.2)"};
    }
    50% {
      box-shadow: ${(props) =>
        props.isRead
          ? "0 0 0 4px rgba(40, 167, 69, 0.4)"
          : "0 0 0 4px rgba(220, 53, 69, 0.4)"};
    }
    100% {
      box-shadow: ${(props) =>
        props.isRead
          ? "0 0 0 2px rgba(40, 167, 69, 0.2)"
          : "0 0 0 2px rgba(220, 53, 69, 0.2)"};
    }
  }
`;

// Modal Styles
export const ModalContent = styled.div`
  padding: 0;
`;

export const ModalHeader = styled.div<{ severity: string }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${(props) => {
    switch (props.severity) {
      case "error":
        return "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)";
      case "warning":
        return "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)";
      case "info":
        return "linear-gradient(135deg, #3498db 0%, #2980b9 100%)";
      default:
        return "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
    }
  }};
  color: white;
  border-radius: 6px 6px 0 0;
`;

export const ErrorIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #262a2a;
  backdrop-filter: blur(10px);

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
`;

export const ModalBody = styled.div`
  padding: 1.5rem;
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
`;

export const AlarmInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .label {
      font-size: 0.85rem;
      color: ${theme.colors.textSecondary};
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .value {
      font-size: 1rem;
      color: ${theme.colors.textPrimary};
      font-weight: 600;
    }
  }
`;

export const TroubleshootingSection = styled.div`
  margin-top: 1.5rem;

  .section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    margin-bottom: 1rem;

    i {
      color: ${theme.colors.tertiary};
    }
  }
`;

export const TroubleshootingList = styled.ol`
  margin: 0;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.75rem;
    line-height: 1.6;
    color: ${theme.colors.textSecondary};

    &::marker {
      color: ${theme.colors.tertiary};
      font-weight: bold;
    }
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  background: ${theme.colors.bgLight};
  margin: 0 -1rem -1rem -1rem;
  border-radius: 0 0 6px 6px;
  border-top: 1px solid ${theme.colors.border};
`;

// ─── Unified Table Card ──────────────────────────────────────────────────────
export const TableCard = styled.div`
  border: none;
  border-radius: 0;
  overflow: visible;
  background: transparent;
  box-shadow: none;
`;

export const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 1rem;
  background: transparent;
  border: none;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

export const ToolbarCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  white-space: nowrap;

  .pi {
    color: inherit;
    opacity: 0.7;
    font-size: 1rem;
  }
`;

export const FilteredBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${theme.colors.textSecondary};
  background: ${theme.colors.bgLight};
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
`;

export const ToolbarFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  .p-input-icon-left {
    display: flex;
    align-items: center;

    > i {
      position: absolute;
      left: 0.65rem;
      color: ${theme.colors.textMuted};
      font-size: 0.85rem;
      pointer-events: none;
      z-index: 1;
    }
  }

  .p-inputtext {
    height: 2.125rem;
    font-size: 0.875rem;
    padding: 0 0.75rem 0 2rem;
    border-radius: 6px;
    width: 165px;
    background: ${theme.colors.bgLight};
    border: 1px solid ${theme.colors.border};
    color: ${theme.colors.textPrimary};

    &::placeholder {
      color: ${theme.colors.textMuted};
    }

    &:enabled:focus {
      background: ${theme.colors.surface};
      border-color: ${theme.colors.tertiary};
      box-shadow: none;
      color: ${theme.colors.textPrimary};
      outline: none;
    }
  }

  .p-dropdown {
    height: 2.125rem;
    border-radius: 6px;
    background: ${theme.colors.bgLight};
    border: 1px solid ${theme.colors.border};
    align-items: center;

    .p-dropdown-label {
      font-size: 0.875rem;
      padding: 0 0.75rem;
      line-height: 2.125rem;
      color: ${theme.colors.textPrimary};
    }

    .p-dropdown-trigger {
      color: ${theme.colors.textSecondary};
      border-left: none;
    }

    &:not(.p-disabled):hover {
      border-color: ${theme.colors.tertiary};
      background: ${theme.colors.surface};
    }

    &.p-focus,
    &.p-inputwrapper-focus {
      border-color: ${theme.colors.tertiary};
      box-shadow: none;
    }
  }

  .p-button.p-button-text {
    height: 2.125rem;
    color: ${theme.colors.textSecondary};
    border: 1px solid ${theme.colors.border};
    border-radius: 6px;
    font-size: 0.875rem;

    &:hover {
      background: ${theme.colors.bgLight};
      color: ${theme.colors.textPrimary};
    }
  }
`;

export const TablePaginator = styled.div`
  border-top: 1px solid ${theme.colors.border};
  background: transparent;

  .p-paginator {
    background: transparent;
    border: none;
    padding: 0.5rem 0;
  }
`;

export const AlarmListContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: transparent;
`;

export const AlarmTableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 230px 200px 1fr 250px 100px 50px;
  gap: 1rem;
  align-items: center;
  padding: 0.875rem 0;
  min-height: 2.75rem;
  background: transparent;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.4;
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border: none;
  border-bottom: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    display: none;
  }
`;

export const AlarmListItem = styled.div`
  display: grid;
  grid-template-columns: 50px 230px 200px 1fr 250px 100px 50px;
  gap: 1rem;
  align-items: center;
  padding: 0.875rem 0;
  border-bottom: 1px solid ${theme.colors.border};
  background: transparent;
  transition: background 0.15s ease;

  &:nth-child(even) {
    background: ${theme.colors.bgLight};
  }

  &:hover {
    background: ${theme.colors.tertiaryTransparent};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 0;
    grid-template-columns: none;
    border-left: none;
    border-right: none;
    border-radius: 0;
  }
`;

export const AlarmValue = styled.span`
  font-weight: 600;
  color: ${theme.colors.tertiary};
  font-size: 1.1rem;
  padding: 0.25rem 0.5rem;
  background: transparent;
  border-radius: 0;
  border: none;
`;

export const AlarmTimestamp = styled.span`
  color: ${theme.colors.textSecondary};
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0;
  background: transparent;
  border-radius: 0;
`;

export const AlarmTroubleshootingStep = styled.div`
  font-size: 0.8rem;
  line-height: 1.3;

  @media (max-width: 768px) {
    max-width: none;
    margin: 0.5rem 0;
  }
`;

export const AlarmHeaderCell = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.4;
  overflow: visible;
`;

// Modern Card Elements
export const AlarmCardCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    &:before {
      content: attr(data-label);
      font-weight: 600;
      font-size: 0.75rem;
      color: ${theme.colors.textSecondary};
      text-transform: uppercase;
      letter-spacing: 0.5px;
      min-width: 80px;
    }

    justify-content: space-between;
    border-bottom: 1px solid ${theme.colors.borderLight};
    padding-bottom: 0.5rem;

    &:last-child {
      border-bottom: none;
      justify-content: center;
    }
  }
`;

export const AlarmIdBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  height: 32px;
  color: ${theme.colors.textPrimary};
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
`;

export const AlarmTypeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
